import { useRef, useState } from "react";
import {
    StriimTypography,
    StriimForm,
    StriimRadioGroup,
    StriimControlLabel,
    StriimRadio,
    StriimCheckbox,
    StriimInputField,
    StriimButton,
    StriimModal,
    StriimToast,
    StriimTooltip,
    ClearMemory
} from "@striim/striim-ui";
import * as Yup from "yup";
import { Box, Grid, InputAdornment } from "@mui/material";

import { ssoLabels, SIGNATURE_ALGORITHM_OPTIONS } from "./sso.common";
import { styles } from "./sso-container.styles";
import api from "../../../../../../../../core/api/api";
import { getSSOProperties } from "../../users-page";
import FileSelectModalContent from "./file-select-modal-content";
import FolderIcon from "src/generic/icon/manage-striim/folder.svg";
import Password from "src/generic/fd-password/fd-password";

// IdPs that support signing AuthnRequests. Only these expose the "Sign AuthnRequests"
// checkbox and SP key fields; Entra/Okta intentionally hide the signing UI entirely.
const SIGNING_SUPPORTED_IDPS = ["PingFederate"];

const SsoAddConfiguration = ({ setAddSso, setSsoProperties, setEdit, edit, editValues }) => {
    const formRef = useRef();
    const [showModal, setShowModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState("");
    const [showKeystoreModal, setShowKeystoreModal] = useState(false);
    const [selectedKeystoreFile, setSelectedKeystoreFile] = useState("");

    const values =
        edit && editValues
            ? {
                  ...editValues,
                  // Normalize to a real boolean: the backend stores and returns this as a string
                  // ("true"/"false"), but Yup `when(…, { is: true })` uses strict equality and the
                  // checkbox `checked={!!values.signAuthnRequests}` would treat "false" as truthy.
                  signAuthnRequests: editValues.signAuthnRequests === true || editValues.signAuthnRequests === "true",
                  // The backend never returns the real keystore password; getSSOProperties
                  // returns a "********" masked placeholder when one is stored. Represent that as
                  // the encrypted/masked object the Password field understands, so it renders
                  // masked dots (not a literal pre-filled value). Empty when none is stored.
                  spKeystorePassword: editValues.spKeystorePassword ? { encrypted: true, length: 10 } : "",
                  // react-select needs a full {label, value} object to pre-select; plain strings from the backend show empty.
                  signatureAlgorithm:
                      SIGNATURE_ALGORITHM_OPTIONS.find(o => o.value === editValues.signatureAlgorithm) ??
                      editValues.signatureAlgorithm ??
                      null
              }
            : {
                  idp: "Microsoft Entra",
                  applicationID: "",
                  certPath: "",
                  loginURL: "",
                  signAuthnRequests: false,
                  spKeystorePath: "",
                  spKeystoreType: "PKCS12",
                  spKeyAlias: "",
                  spKeystorePassword: "",
                  signatureAlgorithm: "RSA-SHA256"
              };
    const validationSchema = Yup.object().shape({
        idp: Yup.string().required("An option must be selected"),
        applicationID: Yup.string().required("Required"),
        certPath: Yup.string()
            .required("Certificate Path is required")
            .test("pem-or-cer-file", "This file format is not supported. Supported formats *.pem, *.cer", value => {
                if (!value) return false;
                const lowerCased = String(value).toLowerCase();
                return lowerCased.endsWith(".pem") || lowerCased.endsWith(".cer");
            }),
        loginURL: Yup.string()
            .required("Required")
            .url("Please enter a valid URL"),
        spKeystorePath: Yup.string().when("signAuthnRequests", {
            is: true,
            then: s =>
                s
                    .required("SP Keystore Path is required")
                    .test(
                        "p12-or-pfx-file",
                        "This file format is not supported. Supported formats *.p12, *.pfx",
                        value => {
                            if (!value) return false;
                            const lowerCased = String(value).toLowerCase();
                            return lowerCased.endsWith(".p12") || lowerCased.endsWith(".pfx");
                        }
                    )
        }),
        spKeyAlias: Yup.string().when("signAuthnRequests", {
            is: true,
            then: s => s.required("Required")
        }),
        // mixed() because the edit flow seeds this with a masked-placeholder object; a new
        // password is only required on create (on edit, blank keeps the existing one).
        spKeystorePassword: Yup.mixed().when("signAuthnRequests", {
            is: true,
            then: s =>
                edit ? s : s.test("required", "Required", value => typeof value === "string" && value.trim().length > 0)
        })
    });

    const handleSubmit = () => {
        const {
            idp,
            certPath,
            applicationID,
            loginURL,
            signAuthnRequests,
            spKeystorePath,
            spKeystoreType,
            spKeyAlias,
            spKeystorePassword,
            signatureAlgorithm
        } = formRef.current.values;

        // On edit the field is seeded with the masked-placeholder object; if the admin did not
        // type a new password it stays an object — send "" so the backend keeps the existing
        // one (it treats a blank value or the "********" placeholder as "no change").
        const keystorePassword = spKeystorePassword && typeof spKeystorePassword === "object" ? "" : spKeystorePassword;

        // StriimInputField select stores the full option object {label, value} in Formik when the user picks from the dropdown. Extract the raw string before sending to the backend.
        const keystoreType = spKeystoreType && typeof spKeystoreType === "object" ? spKeystoreType.value : spKeystoreType;
        const resolvedSignatureAlgorithm = signatureAlgorithm && typeof signatureAlgorithm === "object" ? signatureAlgorithm.value : signatureAlgorithm;

        createSSO(
            idp,
            certPath,
            applicationID,
            loginURL,
            signAuthnRequests === true || signAuthnRequests === "true",
            spKeystorePath,
            keystoreType,
            spKeyAlias,
            keystorePassword,
            resolvedSignatureAlgorithm,
            setSsoProperties
        );
    };
    const handleCancel = () => {
        setEdit(false);
        setAddSso(false);
    };
    const createSSO = async (
        idp,
        certPath,
        applicationID,
        loginURL,
        signAuthnRequests,
        spKeystorePath,
        spKeystoreType,
        spKeyAlias,
        spKeystorePassword,
        signatureAlgorithm,
        setSsoProperties
    ) => {
        try {
            const response = await api.createAdminSSOProperties(
                idp,
                certPath,
                applicationID,
                loginURL,
                signAuthnRequests,
                spKeystorePath,
                spKeystoreType,
                spKeyAlias,
                spKeystorePassword,
                signatureAlgorithm
            );

            if (response) {
                StriimToast({
                    message: "Single Sign On created",
                    severity: "success"
                });
                getSSOProperties(setSsoProperties, handleCancel);
            }
        } catch (error) {
            console.error(error);
            StriimToast({
                message: error,
                severity: "error"
            });
        }
    };

    return (
        <StriimForm
            values={values}
            formRef={formRef}
            validationSchema={validationSchema}
            validateOnMount
            validateOnChange
            onSubmit={() => {
                handleSubmit();
            }}
        >
            {({ values, handleChange, setFieldValue, setFieldTouched, isValid }) => {
                const selectedSsoOption = ssoLabels.find(option => option.idp === values.idp);

                return (
                    <>
                        <Grid container direction={"column"} data-test-id="sso-form-container" gap={2}>
                            <Grid item sx={styles.addSSOHeader}>
                                <StriimTypography variant="h3" sx={styles.heading}>
                                    Add Single Sign On
                                </StriimTypography>
                            </Grid>

                            <Grid item xs={4} sx={styles.addSsoContent}>
                                <StriimTypography variant="caption3" sx={styles.addSsoCaption}>
                                    Identity Provider (IDP)
                                </StriimTypography>

                                <StriimRadioGroup
                                    required
                                    value={values.idp}
                                    onChange={async event => {
                                        await setFieldValue("idp", event.target.value);
                                        await setFieldValue("certPath", "");
                                        await setFieldValue("applicationID", "");
                                        await setFieldValue("loginURL", "");
                                        await setFieldValue("signAuthnRequests", false);
                                        await setFieldValue("spKeystorePath", "");
                                        await setFieldValue("spKeyAlias", "");
                                        await setFieldValue("spKeystorePassword", "");
                                        await setFieldValue("spKeystoreType", "PKCS12");
                                        await setFieldValue("signatureAlgorithm", "RSA-SHA256");
                                        handleChange(event);
                                    }}
                                    name="idp"
                                    isFormElement
                                    id="sso-idp"
                                    sx={styles.addSsoRadioGroup}
                                >
                                    {ssoLabels.map((item, key) => (
                                        <StriimControlLabel
                                            key={key}
                                            value={item.idp}
                                            label={item.idp}
                                            isFormElement
                                            control={<StriimRadio checked={values.idp === item.idp} />}
                                            data-test-id={`idp-option-${item.idp}`}
                                        />
                                    ))}
                                </StriimRadioGroup>
                            </Grid>

                            <Grid item xs={4} sx={styles.addSsoContent}>
                                <Box sx={styles.addSsoContentBox}>
                                    <StriimInputField
                                        id="sso-form-app-id"
                                        xs={3}
                                        isFormElement
                                        required
                                        name="applicationID"
                                        label={selectedSsoOption.applicationID}
                                        placeholder={selectedSsoOption.appIdPlaceholder}
                                    ></StriimInputField>
                                </Box>
                            </Grid>

                            <Grid item xs={4} sx={styles.addSsoContent}>
                                <Box sx={[styles.labelWrap, styles.addSsoContentBox]}>
                                    <StriimInputField
                                        id="sso-form-certificate-path"
                                        xs={3}
                                        isFormElement
                                        required
                                        name="certPath"
                                        label={selectedSsoOption.certPathLabel}
                                        placeholder={selectedSsoOption.certPathPlaceholder}
                                        InputProps={{
                                            onClick: () => setShowModal(true),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <FolderIcon style={{ fontSize: "20px", fill: "none" }} />
                                                </InputAdornment>
                                            )
                                        }}
                                        helperText={selectedSsoOption.certPathHelper}
                                    ></StriimInputField>
                                </Box>
                            </Grid>

                            <Grid item xs={4} sx={styles.addSsoContent}>
                                <Box sx={styles.addSsoContentBox}>
                                    <StriimInputField
                                        id="sso-form-url"
                                        xs={3}
                                        isFormElement
                                        required
                                        name="loginURL"
                                        label={selectedSsoOption.loginURL}
                                        placeholder={selectedSsoOption.urlPlaceholder}
                                    ></StriimInputField>
                                </Box>
                            </Grid>

                            {SIGNING_SUPPORTED_IDPS.includes(values.idp) && (
                                <>
                                    <Grid item xs={4} sx={styles.addSsoContent}>
                                        <StriimControlLabel
                                            control={
                                                <StriimCheckbox
                                                    checked={!!values.signAuthnRequests}
                                                    onChange={e => {
                                                        setFieldValue("signAuthnRequests", e.target.checked);
                                                    }}
                                                    name="signAuthnRequests"
                                                />
                                            }
                                            label={selectedSsoOption.signAuthnRequests}
                                        />
                                    </Grid>

                                    {values.signAuthnRequests && (
                                        <>
                                            <Grid item xs={4} sx={styles.addSsoContent}>
                                                <Box sx={[styles.labelWrap, styles.addSsoContentBox]}>
                                                    <StriimInputField
                                                        id="sso-form-keystore-path"
                                                        xs={3}
                                                        isFormElement
                                                        required
                                                        name="spKeystorePath"
                                                        label={selectedSsoOption.spKeystorePath}
                                                        placeholder={selectedSsoOption.spKeystorePathPlaceholder}
                                                        InputProps={{
                                                            onClick: () => setShowKeystoreModal(true),
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <FolderIcon
                                                                        style={{ fontSize: "20px", fill: "none" }}
                                                                    />
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                        helperText={selectedSsoOption.spKeystorePathHelper}
                                                    ></StriimInputField>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={4} sx={styles.addSsoContent}>
                                                <Box sx={styles.addSsoContentBox}>
                                                    <StriimInputField
                                                        id="sso-form-key-alias"
                                                        xs={3}
                                                        isFormElement
                                                        required
                                                        name="spKeyAlias"
                                                        label={selectedSsoOption.spKeyAlias}
                                                        placeholder={selectedSsoOption.spKeyAliasPlaceholder}
                                                    ></StriimInputField>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={4} sx={styles.addSsoContent}>
                                                <Box sx={styles.addSsoContentBox}>
                                                    <Password
                                                        id="sso-form-keystore-password"
                                                        xs={3}
                                                        required={!edit}
                                                        name="spKeystorePassword"
                                                        label={selectedSsoOption.spKeystorePassword}
                                                        placeholder={
                                                            edit
                                                                ? selectedSsoOption.spKeystorePasswordEditPlaceholder
                                                                : selectedSsoOption.spKeystorePasswordPlaceholder
                                                        }
                                                        enabled
                                                        previewPassword
                                                        value={values.spKeystorePassword}
                                                        onChange={value => setFieldValue("spKeystorePassword", value)}
                                                    />
                                                </Box>
                                            </Grid>

                                            <Grid item xs={4} sx={styles.addSsoContent}>
                                                <Box sx={styles.addSsoContentBox}>
                                                    <StriimInputField
                                                        id="sso-form-signature-algorithm"
                                                        xs={3}
                                                        isFormElement
                                                        select
                                                        name="signatureAlgorithm"
                                                        label={selectedSsoOption.signatureAlgorithm}
                                                        SelectProps={{
                                                            options: SIGNATURE_ALGORITHM_OPTIONS,
                                                            menuPosition: "fixed"
                                                        }}
                                                    ></StriimInputField>
                                                </Box>
                                            </Grid>
                                        </>
                                    )}
                                </>
                            )}
                        </Grid>

                        <Grid item xs={12} sx={styles.addSsoButtonsSection}>
                            <StriimButton onClick={handleCancel} variant="primaryText">
                                Cancel
                            </StriimButton>
                            <StriimButton
                                onClick={handleSubmit}
                                variant="primary"
                                disabled={!isValid}
                                data-test-id="sso-form-save"
                            >
                                Save
                            </StriimButton>
                        </Grid>
                        {showModal && (
                            <StriimModal
                                size="medium"
                                autoHeight
                                isVisible={showModal}
                                footerBorder
                                footerContent={
                                    <Grid container justifyContent="space-between" alignItems="center">
                                        <Grid item sx={styles.selectedFileName}>
                                            {selectedFile && (
                                                <>
                                                    <StriimTypography variant="caption3" sx={styles.modalFooterText}>
                                                        Selected File{" "}
                                                    </StriimTypography>
                                                    <StriimTooltip title={selectedFile.name}>
                                                        <StriimTypography
                                                            variant="caption3"
                                                            color="secondary.500"
                                                            sx={styles.modalFooterText}
                                                        >
                                                            {selectedFile.name}
                                                        </StriimTypography>
                                                    </StriimTooltip>
                                                </>
                                            )}
                                        </Grid>
                                        <Grid item>
                                            <StriimButton
                                                variant="text"
                                                onClick={() => {
                                                    setSelectedFile("");
                                                    ClearMemory();
                                                    setShowModal(false);
                                                }}
                                            >
                                                Cancel
                                            </StriimButton>
                                            <StriimButton
                                                disabled={!selectedFile}
                                                data-test-id={"confirm-select-file"}
                                                onClick={() => {
                                                    ClearMemory();
                                                    setShowModal(false);
                                                    setFieldValue(
                                                        "certPath",
                                                        selectedFile?.currentPath || selectedFile?.path
                                                    );
                                                }}
                                                variant="primary"
                                            >
                                                Done
                                            </StriimButton>
                                        </Grid>
                                    </Grid>
                                }
                            >
                                <FileSelectModalContent
                                    selectedFile={selectedFile}
                                    onCancel={() => setShowModal(false)}
                                    onSelected={file => {
                                        setSelectedFile(file);
                                    }}
                                    allowedExtensions={[".pem", ".cer"]}
                                />
                            </StriimModal>
                        )}
                        {showKeystoreModal && (
                            <StriimModal
                                size="medium"
                                autoHeight
                                isVisible={showKeystoreModal}
                                footerBorder
                                footerContent={
                                    <Grid container justifyContent="space-between" alignItems="center">
                                        <Grid item sx={styles.selectedFileName}>
                                            {selectedKeystoreFile && (
                                                <>
                                                    <StriimTypography variant="caption3" sx={styles.modalFooterText}>
                                                        Selected File{" "}
                                                    </StriimTypography>
                                                    <StriimTooltip title={selectedKeystoreFile.name}>
                                                        <StriimTypography
                                                            variant="caption3"
                                                            color="secondary.500"
                                                            sx={styles.modalFooterText}
                                                        >
                                                            {selectedKeystoreFile.name}
                                                        </StriimTypography>
                                                    </StriimTooltip>
                                                </>
                                            )}
                                        </Grid>
                                        <Grid item>
                                            <StriimButton
                                                variant="text"
                                                onClick={() => {
                                                    setSelectedKeystoreFile("");
                                                    ClearMemory();
                                                    setShowKeystoreModal(false);
                                                }}
                                            >
                                                Cancel
                                            </StriimButton>
                                            <StriimButton
                                                disabled={!selectedKeystoreFile}
                                                data-test-id={"confirm-select-keystore-file"}
                                                onClick={() => {
                                                    ClearMemory();
                                                    setShowKeystoreModal(false);
                                                    setFieldValue(
                                                        "spKeystorePath",
                                                        selectedKeystoreFile?.currentPath || selectedKeystoreFile?.path
                                                    );
                                                    // The path is set programmatically, so mark the field touched
                                                    // to surface the .p12/.pfx format error when the picked file is
                                                    // not a supported keystore (otherwise Save just greys out with
                                                    // no explanation).
                                                    setFieldTouched("spKeystorePath", true);
                                                }}
                                                variant="primary"
                                            >
                                                Done
                                            </StriimButton>
                                        </Grid>
                                    </Grid>
                                }
                            >
                                <FileSelectModalContent
                                    selectedFile={selectedKeystoreFile}
                                    onCancel={() => setShowKeystoreModal(false)}
                                    onSelected={file => {
                                        setSelectedKeystoreFile(file);
                                    }}
                                    allowedExtensions={[".p12", ".pfx"]}
                                />
                            </StriimModal>
                        )}
                    </>
                );
            }}
        </StriimForm>
    );
};

export default SsoAddConfiguration;
